import React, { useEffect, useState } from 'react';
import { useParams, useSearch } from 'wouter';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getQueryFn, apiRequest } from '@/lib/queryClient';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Pencil, Plus, Save, Trash2, X } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface InstoreLocation {
  id: number;
  name: string;
  retailerId?: number;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export default function RetailerInstoreLocationsPage({ retailerIdOverride }: { retailerIdOverride?: number } = {}) {
  const params = useParams() as { retailerId?: string };
  const search = useSearch();
  const retailerId = String(retailerIdOverride ?? (params?.retailerId ? Number(params.retailerId) : ''));
  const queryClient = useQueryClient();
  const [newName, setNewName] = useState('');
  const requestedLocationId = Number(new URLSearchParams(search).get('locationId'));
  const [editingLocationId, setEditingLocationId] = useState<number | null>(
    Number.isFinite(requestedLocationId) && requestedLocationId > 0 ? requestedLocationId : null,
  );
  const [editName, setEditName] = useState('');
  const hasNewName = newName.trim().length > 0;
  const { toast } = useToast();

  const locationsQuery = useQuery<{ success: boolean; locations: InstoreLocation[] } | null>({
    queryKey: [`/api/admin/instore-locations?retailerId=${retailerId}`],
    queryFn: getQueryFn({ on401: 'returnNull' }),
  });

  const addMutation = useMutation({
    mutationFn: async (name: string) => {
      await apiRequest(`/api/admin/instore-locations`, 'POST', { retailerId: Number(retailerId), name });
    },
    onSuccess: () => {
      setNewName('');
      queryClient.invalidateQueries({ queryKey: [`/api/admin/instore-locations?retailerId=${retailerId}`] });
      toast({
        title: 'Location Added',
        description: 'New in-store location created successfully.',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Add Failed',
        description: error.message || 'Failed to create location.',
        variant: 'destructive',
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const response = await apiRequest(`/api/admin/instore-locations/${id}`, 'DELETE');
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || 'Failed to delete location');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/admin/instore-locations?retailerId=${retailerId}`] });
      setEditingLocationId(null);
      setEditName('');
      toast({
        title: 'Location Deleted',
        description: 'In-store location removed successfully.',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Delete Failed',
        description: error.message || 'Failed to delete location.',
        variant: 'destructive',
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, name }: { id: number; name: string }) => {
      const response = await apiRequest(`/api/admin/instore-locations/${id}`, 'PATCH', { name });
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || 'Failed to update location');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/admin/instore-locations?retailerId=${retailerId}`] });
      setEditingLocationId(null);
      setEditName('');
      toast({
        title: 'Location Updated',
        description: 'The new name will flow through to store profiles and posting instructions.',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Update Failed',
        description: error.message || 'Failed to update location.',
        variant: 'destructive',
      });
    },
  });

  const handleAdd = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newName.trim()) return;
    addMutation.mutate(newName.trim());
  };

  const data = locationsQuery.data?.locations || [];

  useEffect(() => {
    if (editingLocationId == null || editName) return;
    const location = data.find((item) => item.id === editingLocationId);
    if (location) {
      setEditName(location.name);
      requestAnimationFrame(() => {
        document.getElementById(`instore-location-${location.id}`)?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      });
    }
  }, [data, editName, editingLocationId]);

  return (
    <div className="p-4">
      <h1 className="text-xl font-semibold mb-4">In-Store Locations</h1>

      <form onSubmit={handleAdd} className="mb-4 space-y-2">
        <div className="flex gap-2">
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Add a new location name"
            className="border rounded px-3 py-2 w-64"
          />
          <Button
            type="submit"
            disabled={!hasNewName || addMutation.isPending}
            aria-disabled={!hasNewName || addMutation.isPending}
          >
            <Plus className="h-4 w-4 mr-2" />
            {addMutation.isPending ? 'Adding…' : 'Add'}
          </Button>
        </div>
        {!hasNewName && !addMutation.isPending ? (
          <p className="text-sm text-gray-500">Enter a location name to enable Add.</p>
        ) : null}
      </form>

      {locationsQuery.isLoading ? (
        <div>Loading…</div>
      ) : locationsQuery.isError ? (
        <div className="text-red-600">Error loading data</div>
      ) : !data || data.length === 0 ? (
        <div className="text-gray-600">No locations defined for this retailer yet. Add locations above to enable posting instruction management.</div>
      ) : (
        <div className="overflow-auto">
          <table className="min-w-[500px] w-full border border-gray-200 bg-white">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-3 py-2 border-b">Name</th>
                <th className="text-left px-3 py-2 border-b">Created</th>
                <th className="text-left px-3 py-2 border-b">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.map((loc) => (
                <tr
                  key={loc.id}
                  id={`instore-location-${loc.id}`}
                  className={editingLocationId === loc.id ? 'bg-blue-50' : 'hover:bg-gray-50'}
                >
                  <td className="px-3 py-2 border-b">
                    {editingLocationId === loc.id ? (
                      <input
                        type="text"
                        value={editName}
                        onChange={(event) => setEditName(event.target.value)}
                        className="w-full min-w-56 rounded border px-2.5 py-1.5"
                        aria-label={`Rename ${loc.name}`}
                        autoFocus
                      />
                    ) : loc.name}
                  </td>
                  <td className="px-3 py-2 border-b text-gray-700">{loc.createdAt ? new Date(loc.createdAt).toLocaleString() : '-'}</td>
                  <td className="px-3 py-2 border-b">
                    <div className="flex flex-wrap gap-1">
                      {editingLocationId === loc.id ? (
                        <>
                          <Button
                            type="button"
                            size="sm"
                            disabled={!editName.trim() || updateMutation.isPending}
                            onClick={() => updateMutation.mutate({ id: loc.id, name: editName.trim() })}
                          >
                            <Save className="h-4 w-4 mr-1" />
                            {updateMutation.isPending ? 'Saving…' : 'Save'}
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setEditingLocationId(null);
                              setEditName('');
                            }}
                          >
                            <X className="h-4 w-4 mr-1" />
                            Cancel
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setEditingLocationId(loc.id);
                              setEditName(loc.name);
                            }}
                          >
                            <Pencil className="h-4 w-4 mr-1" />
                            Rename
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                disabled={deleteMutation.isPending}
                              >
                                <Trash2 className="h-4 w-4 mr-1" />
                                Delete
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete “{loc.name}”?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This removes the retailer-wide location. It cannot be deleted while campaign posting instructions still reference it. To remove only its shape from one store, use Manage Twin instead.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  className="bg-red-600 hover:bg-red-700"
                                  onClick={() => deleteMutation.mutate(loc.id)}
                                >
                                  Delete location
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
